import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { AlertDialog, IconBrick, PackageRow, TopNavigation } from "@hamlak/ui";
import type { Product } from "@/api/types";
import { analytics, type StoreSource } from "@/analytics";
import { periodText, t } from "@/i18n/ko";
import { bonusLabel, isHighlighted, productName } from "@/i18n/codes";
import { useAuthStore } from "@/stores/authStore";
import { useConfigStore } from "@/stores/configStore";
import { EmptyState } from "@/components/EmptyState";
import { productPrice } from "./money";
import { firstPurchaseOffer, useStoreProducts, type StoreItem } from "./useStoreProducts";
import { isStoreFailure, usePurchase } from "./usePurchase";
import { paragraphs } from "@/features/system/NoticeDialogs";
import { useUiStore } from "@/stores/uiStore";
import { platform } from "@/platform";
import { StoreUnsupportedPage } from "./web/StoreUnsupportedPage";
import { useStoreRegion } from "./web/useStoreRegion";
import { isTossFailure, useWebCheckout } from "./web/webCheckout";
import "./store.css";

/** 서버 가격 행 표시(스토어 가격이 없을 때의 폴백·웹). 실제 목록은 useStoreProducts 가 스토어 priceString 을 우선한다(결정 8) */
export const priceLabel = (p: Product) => productPrice(p);

/** /store 로 보내는 라우터 state. 진입 경로(view_store.store_source). 없으면 other */
/** paymentFailed: 웹 결제창 실패로 돌아옴(WebFailPage) → F06-03 다이얼로그. 값은 [다시 시도] 할 상품 ID(모르면 null) */
export type StoreState = { storeSource?: StoreSource; paymentFailed?: string | null };

/**
 * F06-02 상점(1.14). GET /store/products를 렌더: 표시 목록은 서버가 정한다(결제 이력 0건이면 `.first` 특가, AC0-258). 특가가 있으면 첫 결제 배너, 특가 행은 할인율·원가 취소선.
 * 행 = "브릭 {bricks}개"(기본 수량) + 보너스 칩 "+{bonus}"(지급 = bricks + bonus, AC0-264), popular 는 강조 행(AC0-263). 하단 안내 2줄에 lotPolicy.welcome.ttlDays·pricing.refundWindowDays를 끼운다. 구매 → usePurchase(1.15).
 */
export function StorePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const balance = useAuthStore((s) => s.balance);
  const pricing = useConfigStore((s) => s.settings?.pricing);
  // 웹(결정 10·17): 계정 국가가 지원 국가일 때만 상점, 아니면 결제 불가 안내. 앱은 스토어가 판단한다
  const isWeb = platform.clientKind === "web";
  const region = useStoreRegion(isWeb);
  const webSupported = !isWeb || region.data?.webPurchaseSupported === true;
  const products = useStoreProducts(webSupported);
  const purchase = usePurchase();
  const webCheckout = useWebCheckout();
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const [returnedFailure, setReturnedFailure] = useState<string | null | undefined>(() => (location.state as StoreState | null)?.paymentFailed);

  // 결정 8: 서버 목록 ∩ 기기 스토어. 스토어 조회 실패면 목록은 보이되 결제 버튼이 잠긴다
  const visible = (products.data?.items ?? []).filter((i) => i.product.available);
  const storeUnavailable = products.data?.storeUnavailable ?? false;
  const unavailableNoticed = useRef(false);
  useEffect(() => {
    if (!storeUnavailable || unavailableNoticed.current) return;
    unavailableNoticed.current = true;
    showSnackbar(t("store.storeUnavailable"));
  }, [storeUnavailable, showSnackbar]);
  const viewSent = useRef(false);
  useEffect(() => {
    // 진입 1회. StrictMode 의 effect 재실행에도 ref 는 유지된다
    if (viewSent.current) return;
    viewSent.current = true;
    analytics.track("view_store", { store_source: (location.state as StoreState | null)?.storeSource ?? "other" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const freeTtlDays = pricing?.lotPolicy.welcome?.ttlDays ?? null;

  // 첫 결제 배너(시안 43:734 193:11033): 모든 행이 같은 할인율의 특가면 "모든 상품 N% 할인", 아니면 일반 문구
  const { active: hasFirst, allRate } = firstPurchaseOffer(visible);
  const discountOf = (i: StoreItem) =>
    i.product.firstPurchaseOnly && i.product.discountPercent !== null && i.regularPriceLabel
      ? { rate: t("store.discountRate", { percent: i.product.discountPercent }), original: i.regularPriceLabel }
      : undefined;
  const buying = purchase.isPending || webCheckout.isPending;
  const buy = (i: StoreItem) => {
    if (!i.purchasable) return showSnackbar(t("store.storeUnavailable"));
    if (isWeb) webCheckout.mutate(i.product);
    else purchase.mutate(i.product);
  };

  // F06-03 결제 실패 다이얼로그: 앱 스토어 단계 실패 · 웹 결제창 실패 · 웹 failUrl 복귀. 값 = [다시 시도] 할 상품 ID(모르면 null), undefined 면 닫힘
  const failedProductId: string | null | undefined =
    purchase.isError && isStoreFailure(purchase.error)
      ? (purchase.variables?.productId ?? null)
      : webCheckout.isError && isTossFailure(webCheckout.error)
        ? (webCheckout.variables?.productId ?? null)
        : returnedFailure;
  const closeFailure = () => {
    purchase.reset();
    webCheckout.reset();
    if (returnedFailure !== undefined) {
      setReturnedFailure(undefined);
      navigate(location.pathname, { replace: true, state: null }); // 새로고침·뒤로가기로 다시 뜨지 않게
    }
  };
  const retryFailure = () => {
    const item = visible.find((i) => i.product.productId === failedProductId);
    closeFailure();
    if (item) buy(item);
  };

  if (isWeb && region.data && !region.data.webPurchaseSupported) return <StoreUnsupportedPage country={region.data.accountCountry} />;
  // 하단 안내 2줄(시안 749:1035): 무료 브릭 만료 · 환불 창
  const notes = pricing
    ? [freeTtlDays !== null ? t("store.note.free", { days: freeTtlDays }) : null, t("store.note.refund", { period: periodText(pricing.refundWindowDays) })].filter((x): x is string => !!x)
    : [];

  return (
    <main className="page page--wide" data-testid="store-page">
      {/* 시안(F06-02 첫 결제 전 43:734 · 후 749:1002 · Ultra 402:2661): 헤더는 전폭 "뒤로 + 브릭 상점", 본문 560 중앙 — 보유 브릭 카드 → 첫 결제 배너(특가가 있을 때) → "브릭 충전" → 패키지 → 안내 2줄 */}
      <TopNavigation
        className="store__nav"
        title={t("page.store")}
        onBack={() => navigate(-1)}
        right={null}
      />
      <div className="store">
        {balance !== null && (
          <section className="store__balance" data-testid="store-balance">
            <IconBrick size={32} />
            <div className="store__balanceTexts">
              <span className="store__balanceLabel">
                {t("store.balance.title")}
              </span>
              <span className="store__balanceValue">
                {balance.toLocaleString("ko-KR")}
              </span>
            </div>
            {/* 시안 43:752 "사용 내역 >" t3 brand-contrast 텍스트 링크(시안 hidden, 진입점이라 유지) */}
            <button
              type="button"
              className="store__historyLink"
              onClick={() => navigate("/me/bricks?tab=usage")}
            >
              {t("store.history")}
            </button>
          </section>
        )}
        {products.isPending || (isWeb && region.isPending) ? (
          <div className="page-placeholder" role="status">
            {t("common.loading")}
          </div>
        ) : products.isError ? (
          <EmptyState
            message={t("store.loadError")}
            action={{
              label: t("common.retry"),
              onClick: () => void products.refetch(),
            }}
          />
        ) : visible.length === 0 ? (
          <EmptyState message={t("store.empty")} />
        ) : (
          <>
            {hasFirst && (
              <section className="store__first" data-testid="store-first-purchase">
                <IconBrick size={36} />
                <div className="store__firstTexts">
                  <span className="store__firstTitle">{t("store.firstPurchase.title")}</span>
                  <span className="store__firstHeadline">
                    {allRate !== null ? t("store.firstPurchase.allRate", { percent: allRate }) : t("store.firstPurchase.headline")}
                  </span>
                  <span className="store__firstBody">{t(allRate === 50 ? "store.firstPurchase.half" : "store.firstPurchase.body")}</span>
                </div>
              </section>
            )}
            <h2 className="store__sectionTitle">{t("store.section.topup")}</h2>
            <section className="store__list" data-testid="store-list">
              {visible.map((i) => (
                <PackageRow
                  key={i.product.productId}
                  label={productName(i.product)}
                  badge={bonusLabel(i.product) ?? undefined}
                  highlight={isHighlighted(i.product)}
                  priceLabel={i.priceLabel}
                  discount={discountOf(i)}
                  disabled={buying || !i.purchasable}
                  onClick={() => buy(i)}
                  data-product-id={i.product.productId}
                />
              ))}
            </section>
          </>
        )}
        {notes.length > 0 && (
          <ul className="store__notes" data-testid="store-notes">
            {notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        )}
      </div>
      {/* F06-03(82:2824) 결제 실패: brand pair [닫기] · [다시 시도](같은 상품 재구매) */}
      <AlertDialog
        open={failedProductId !== undefined}
        tone="brand"
        action="pair"
        title={t("store.purchase.failed")}
        description={paragraphs(t("store.purchase.failed.body"))}
        primaryLabel={t("common.retry")}
        secondaryLabel={t("common.close")}
        onPrimary={retryFailure}
        onSecondary={closeFailure}
      />
    </main>
  );
}
